import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, Plus, Trash2, FlaskConical, Loader2 } from "lucide-react";

const GRADE_OPTIONS = ["A", "B", "C", "D"];
const CATEGORY_OPTIONS = [
  { value: "performing", label: "Performing" },
  { value: "semi_performing", label: "Semi-Performing" },
  { value: "non_performing", label: "Non-Performing" },
  { value: "repo_ready", label: "Repo-Ready" },
  { value: "fresh_origination", label: "Fresh Origination" },
];
const CHANNELS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "whatsapp", label: "WhatsApp" },
];

const emptyForm = () => ({
  name: "",
  grade_min: "",
  category: [],
  state: "",
  yield_min: "",
  yield_max: "",
  asking_price_max: "",
  vehicle_make: "",
  channels: ["email"],
  phone_number: "",
});

export default function WatchlistManager() {
  const [items, setItems] = useState(null); // null=loading, []
  const [form, setForm] = useState(null);   // null=hidden, object=visible
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/watchlists");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load watchlists");
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyForm()); setEditingId(null); };
  const startEdit = (w) => {
    setEditingId(w.watchlist_id);
    const c = w.criteria || {};
    setForm({
      name: w.name || "",
      grade_min: c.grade_min || "",
      category: c.category || [],
      state: (c.state || []).join(", "),
      yield_min: c.yield_min ?? "",
      yield_max: c.yield_max ?? "",
      asking_price_max: c.asking_price_max ?? "",
      vehicle_make: (c.vehicle_make || []).join(", "),
      channels: w.channels?.length ? w.channels : ["email"],
      phone_number: w.phone_number || "",
    });
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Give your watchlist a name"); return; }
    if ((form.channels.includes("sms") || form.channels.includes("whatsapp")) && !form.phone_number.trim()) {
      toast.error("Phone number is required for SMS or WhatsApp"); return;
    }
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      criteria: {
        grade_min: form.grade_min || null,
        category: form.category.length ? form.category : null,
        state: form.state ? form.state.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) : null,
        yield_min: form.yield_min === "" ? null : Number(form.yield_min),
        yield_max: form.yield_max === "" ? null : Number(form.yield_max),
        asking_price_max: form.asking_price_max === "" ? null : Number(form.asking_price_max),
        vehicle_make: form.vehicle_make ? form.vehicle_make.split(",").map((s) => s.trim()).filter(Boolean) : null,
      },
      channels: form.channels,
      phone_number: form.phone_number.trim() || null,
      active: true,
    };
    try {
      if (editingId) {
        await api.patch(`/watchlists/${editingId}`, payload);
        toast.success("Watchlist updated");
      } else {
        await api.post("/watchlists", payload);
        toast.success("Watchlist saved — we'll alert you on matches");
      }
      setForm(null); setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this watchlist?")) return;
    await api.delete(`/watchlists/${id}`);
    toast.success("Deleted");
    await load();
  };

  const testMatch = async (id) => {
    const r = await api.post(`/watchlists/${id}/test`);
    toast.info(`Current matches: ${r.data.count}`);
  };

  const toggleArrayValue = (key, val) => {
    setForm((f) => {
      const arr = new Set(f[key]);
      arr.has(val) ? arr.delete(val) : arr.add(val);
      return { ...f, [key]: Array.from(arr) };
    });
  };

  if (items === null) {
    return (
      <div className="border border-border p-8 flex items-center gap-3 text-muted-foreground" data-testid="watchlist-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading watchlists…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="watchlist-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-primary" />
          <div className="font-overline text-accent">Watchlists</div>
        </div>
        {form === null && (
          <Button onClick={startNew} className="rounded-none uppercase tracking-wider"
            data-testid="watchlist-new-btn">
            <Plus className="w-4 h-4 mr-2" /> New Watchlist
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Get alerted the moment a new note matches your criteria — by email, SMS, or WhatsApp.
      </p>

      {form === null && items.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="watchlist-empty">
          No watchlists yet. Create one to start receiving alerts when matching notes hit the marketplace.
        </div>
      )}

      {form === null && items.map((w) => (
        <div key={w.watchlist_id} className="border border-border p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`watchlist-item-${w.watchlist_id}`}>
          <div className="flex-1">
            <div className="font-mono-data text-sm">{w.name}</div>
            <div className="text-xs text-muted-foreground">
              {summarizeCriteria(w.criteria)} · Channels: {(w.channels || []).join(", ")}
              {w.match_count ? ` · ${w.match_count} alerts sent` : ""}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => testMatch(w.watchlist_id)}
              data-testid={`watchlist-test-${w.watchlist_id}`}>
              <FlaskConical className="w-4 h-4 mr-1" /> Test
            </Button>
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => startEdit(w)}
              data-testid={`watchlist-edit-${w.watchlist_id}`}>Edit</Button>
            <Button variant="destructive" size="sm" className="rounded-none" onClick={() => remove(w.watchlist_id)}
              data-testid={`watchlist-delete-${w.watchlist_id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}

      {form !== null && (
        <div className="border border-border p-6 space-y-4" data-testid="watchlist-form">
          <div>
            <Label className="font-overline">Name</Label>
            <Input className="rounded-none mt-2" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="watchlist-name" placeholder="e.g. Texas A-grade 22%+" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-overline">Min Grade</Label>
              <Select value={form.grade_min || "any"} onValueChange={(v) => setForm({ ...form, grade_min: v === "any" ? "" : v })}>
                <SelectTrigger className="rounded-none mt-2" data-testid="watchlist-grade-min"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any grade</SelectItem>
                  {GRADE_OPTIONS.map((g) => <SelectItem key={g} value={g}>{g} or better</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-overline">State (comma-sep)</Label>
              <Input className="rounded-none mt-2" value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                placeholder="TX, FL, CA" data-testid="watchlist-state" />
            </div>
          </div>

          <div>
            <Label className="font-overline">Categories</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {CATEGORY_OPTIONS.map((c) => (
                <label key={c.value} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.category.includes(c.value)} onCheckedChange={() => toggleArrayValue("category", c.value)}
                    data-testid={`watchlist-cat-${c.value}`} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="font-overline">Min Yield %</Label>
              <Input className="rounded-none mt-2 font-mono-data" type="number" value={form.yield_min}
                onChange={(e) => setForm({ ...form, yield_min: e.target.value })}
                data-testid="watchlist-yield-min" />
            </div>
            <div>
              <Label className="font-overline">Max Yield %</Label>
              <Input className="rounded-none mt-2 font-mono-data" type="number" value={form.yield_max}
                onChange={(e) => setForm({ ...form, yield_max: e.target.value })}
                data-testid="watchlist-yield-max" />
            </div>
            <div>
              <Label className="font-overline">Max Asking $</Label>
              <Input className="rounded-none mt-2 font-mono-data" type="number" value={form.asking_price_max}
                onChange={(e) => setForm({ ...form, asking_price_max: e.target.value })}
                data-testid="watchlist-price-max" />
            </div>
          </div>

          <div>
            <Label className="font-overline">Vehicle Makes (comma-sep)</Label>
            <Input className="rounded-none mt-2" value={form.vehicle_make}
              onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })}
              placeholder="Toyota, Honda" data-testid="watchlist-makes" />
          </div>

          <div>
            <Label className="font-overline">Channels</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              {CHANNELS.map((c) => (
                <label key={c.value} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.channels.includes(c.value)} onCheckedChange={() => toggleArrayValue("channels", c.value)}
                    data-testid={`watchlist-ch-${c.value}`} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          {(form.channels.includes("sms") || form.channels.includes("whatsapp")) && (
            <div>
              <Label className="font-overline">Phone Number (E.164)</Label>
              <Input className="rounded-none mt-2 font-mono-data" value={form.phone_number}
                onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                placeholder="+15551234567" data-testid="watchlist-phone" />
              <p className="text-xs text-muted-foreground mt-1">
                Include country code, e.g. <code>+1</code> for US numbers.
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={submit} disabled={busy} className="rounded-none uppercase tracking-wider h-11"
              data-testid="watchlist-save">
              {busy ? "Saving…" : editingId ? "Update Watchlist" : "Save Watchlist"}
            </Button>
            <Button variant="outline" onClick={() => { setForm(null); setEditingId(null); }}
              className="rounded-none uppercase tracking-wider h-11" data-testid="watchlist-cancel">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function summarizeCriteria(c) {
  if (!c) return "Any note";
  const parts = [];
  if (c.grade_min) parts.push(`Grade ≥ ${c.grade_min}`);
  if (c.yield_min != null) parts.push(`Yield ≥ ${c.yield_min}%`);
  if (c.yield_max != null) parts.push(`Yield ≤ ${c.yield_max}%`);
  if (c.asking_price_max != null) parts.push(`Price ≤ $${c.asking_price_max}`);
  if (c.state?.length) parts.push(`States: ${c.state.join(", ")}`);
  if (c.category?.length) parts.push(`Cats: ${c.category.length}`);
  if (c.vehicle_make?.length) parts.push(`Makes: ${c.vehicle_make.join(", ")}`);
  return parts.length ? parts.join(" · ") : "Any note";
}
